import cx from "classnames";

import * as StateLib from "@/Utils/StateLib";
import EditableItemGroup, {
  type Action
} from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import {
  BuildChartSystemFieldOptions,
  BuildSystemFieldOptions,
  enumSystemFieldsMode
} from "@/Utils/DataLib";
import { BigInfoIcon, Button, Select } from "@theorgchartdev/storybook";
import type { ListItemOption } from "@theorgchartdev/storybook";
import { ReRenderCanvas } from "@/Utils/StateLib";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { ValidateSortConfig } from "@coreapp/utils/ValidateSortConfig";
import { appstr } from "@coreapp/utils/appstr";
import { GetFieldId } from "@coreapp/utils/GetFieldId";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList, enumObjClassNames } from "@coreapp/constants";
import { QSortConfig } from "@coreapp/CoreApp/QSortConfig";

type FieldSelectTarget = {
  value: string;
  eventInfo: { fieldDef: any; index: number };
};

type OrderSelectTarget = {
  value: boolean;
  eventInfo: number;
};

type Props = {
  manager: QSortConfig;
  setter: (config: QSortConfig) => void;
  preventApply?: boolean;
  shouldPerformSortCheckpoint?: boolean;
  chartMetadata?: any;
  dataTestIdPrefix?: string;
};

export const BoxSort = (props: Props) => {
  const ApplySort = () => {
    if (props.preventApply) return;

    const wsMgr = StateLib.GetWSMgr();
    const doc = StateLib.GetDoc();
    if (!wsMgr || !doc) return;

    const sortConfig = ValidateSortConfig(props.manager, doc);
    const iWiz = doc.GetIWizData();
    wsMgr.PerformSort(sortConfig, iWiz, props.shouldPerformSortCheckpoint);
    ReRenderCanvas();
  };

  const AddNewSortKey = () => {
    props.manager.AddCriteria(null, null, false);
    props.setter(props.manager);
  };

  const DeleteAllSortKeys = () => {
    props.manager.fSortList = [];
    props.setter(props.manager);
    ApplySort();
  };

  const SetCriteriaField = (target: FieldSelectTarget) => {
    const { fieldDef, index } = target.eventInfo;
    const fieldName = fieldDef ? fieldDef.GetFieldName() : target.value;
    const fieldID = fieldDef ? fieldDef.GetFieldID() : GetFieldId(fieldName);

    const curCriteria = props.manager.GetIndexCriteria(index);

    curCriteria.SetFieldName(fieldName);
    curCriteria.SetFieldID(fieldID); // See QXDoc.prototype.HandleInternalFields

    props.setter(props.manager);
    ApplySort();
  };

  const RemoveSortKey = (index: number) => () => {
    props.manager.RemoveCriteriaAtIndex(index);
    props.setter(props.manager);
    ApplySort();
  };

  const SortDown = (index: number) => () => {
    props.manager.Shuffle(index, false);
    props.setter(props.manager);
    ApplySort();
  };

  const SortUp = (index: number) => () => {
    props.manager.Shuffle(index, true);
    props.setter(props.manager);
    ApplySort();
  };

  const SetCriteriaOrder = (target: OrderSelectTarget) => {
    const curCriteria = props.manager.GetIndexCriteria(target.eventInfo);
    curCriteria.SetSortOrder(target.value);
    props.setter(props.manager);
    ApplySort();
  };

  const GetActionsList = (index: number): Action[] => {
    const prefix = props.dataTestIdPrefix;
    let actions: Action[] = [
      {
        onClick: RemoveSortKey(index),
        iconName: "trash-can",
        iconProps: { color: "error" },
        ...(prefix ? { dataTestId: `${prefix}-remove-btn-${index}` } : {})
      }
    ];

    // Show reorder controls only when there is more than one sort key.
    if ((props.manager.fSortList ?? []).length > 1) {
      actions = (
        [
          {
            onClick: SortUp(index),
            iconName: "caret-up",
            ...(prefix ? { dataTestId: `${prefix}-move-up-btn-${index}` } : {})
          },
          {
            onClick: SortDown(index),
            iconName: "caret-down",
            ...(prefix ? { dataTestId: `${prefix}-move-down-btn-${index}` } : {})
          }
        ] as Action[]
      ).concat(actions);
    }

    return actions;
  };

  const list = props.manager.fSortList ?? [];
  const contentClass = "sidebar-content-container";
  const defaultItemClass = "sidebar-content-item";
  const sortItemClass = "sidebar-flex-row";
  const scrollbarClass = "ocn-list-scrollbar";
  const dividerClass = `${defaultItemClass}--divider`;

  const buildOptions = (index: number) => {
    if (props.chartMetadata) {
      return BuildChartSystemFieldOptions(
        props.chartMetadata,
        SetCriteriaField,
        { index },
        enumSystemFieldsMode.sortable
      );
    }

    const doc = StateLib.GetDoc();
    const className = doc?.IsPositionDocument()
      ? enumObjClassNames.position
      : enumObjClassNames.box;
    return BuildSystemFieldOptions(
      SetCriteriaField,
      { index },
      enumSystemFieldsMode.sortable,
      null,
      [],
      [className] as any
    );
  };

  return (
    <>
      <div className={"sidebar-header-buttons"}>
        <Button
          modifier={"secondary"}
          size={"small"}
          disabled={list.length > 2 || !getFeatureAccess(enumFeaturesList.baseChartSort)}
          iconProps={{
            name: "plus-solid"
          }}
          onClick={AddNewSortKey}
          dataTestId={
            props.dataTestIdPrefix ? `${props.dataTestIdPrefix}-add-btn` : "rsb-sort-add-btn"
          }
        >
          {appstr("Add sort key")}
        </Button>
        <Button
          modifier={"secondary"}
          size={"small"}
          disabled={list.length === 0}
          iconProps={{
            name: "broom-wide"
          }}
          onClick={DeleteAllSortKeys}
          dataTestId={
            props.dataTestIdPrefix ? `${props.dataTestIdPrefix}-clear-btn` : "rsb-sort-clear-btn"
          }
        >
          {appstr("Clear all")}
        </Button>
      </div>
      <div className={cx(contentClass, scrollbarClass)}>
        {list.map((item: any, index: number) => {
          let value = props.manager.GetIndexCriteria(index).GetFieldName() ?? "";
          if (!props.chartMetadata) {
            const doc = StateLib.GetDoc();
            const className = doc?.IsPositionDocument() ? "Position" : "Box";
            value = `${className}-${value}`;
          }

          return (
            <div key={index} className={cx(sortItemClass, defaultItemClass, dividerClass)}>
              <EditableItemGroup actions={GetActionsList(index)}>
                <div className={"ocn-sort-controls"}>
                  <Select
                    optionSize={"small"}
                    dataTestId={
                      props.dataTestIdPrefix
                        ? `${props.dataTestIdPrefix}-field-select-${index}`
                        : `rsb-sort-field-select-${index}`
                    }
                    listProps={FieldSelectListProps({
                      inputProps: {
                        dataTestId: "sort-field-search-input"
                      }
                    })}
                    id={"sort-field-button-" + index}
                    placeholder={appstr("Select sort field")}
                    listWidth={"medium"}
                    value={value}
                    options={buildOptions(index) as ListItemOption[]}
                    position="bottom-end"
                  />
                  <Select
                    id={"sort-order-button-" + index}
                    optionSize={"small"}
                    dataTestId={
                      props.dataTestIdPrefix
                        ? `${props.dataTestIdPrefix}-order-select-${index}`
                        : `rsb-sort-order-select-${index}`
                    }
                    value={item.GetSortOrder()}
                    options={
                      [
                        {
                          clickHandler: SetCriteriaOrder,
                          label: appstr("Ascending"),
                          value: true,
                          eventInfo: index
                        },
                        {
                          clickHandler: SetCriteriaOrder,
                          label: appstr("Descending"),
                          value: false,
                          eventInfo: index
                        }
                      ] as unknown as ListItemOption[]
                    }
                  />
                </div>
              </EditableItemGroup>
            </div>
          );
        })}

        {list.length === 0 && (
          <BigInfoIcon
            color="brand"
            name="arrow-down-a-z"
            message={appstr("No sort keys added yet")}
          />
        )}
      </div>
    </>
  );
};
